import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore'
import { db } from '../lib/firebase'
import type {
  ChatMessage,
  Participant,
  RoomState,
  Session,
  WinnerRecord,
} from '../types/session'

const MESSAGE_LIMIT = 300

export function useRoom(session: Session | null) {
  const roomCode = session?.roomCode ?? null
  const isHost = session?.role === 'admin'

  const [room, setRoom] = useState<RoomState>({
    name: 'Live draw',
    chatEnabled: true,
    closed: false,
    startsAt: null,
    timeZone: null,
    lastWinner: null,
  })
  const [participants, setParticipants] = useState<Participant[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [winners, setWinners] = useState<WinnerRecord[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!roomCode) return
    const onError = (e: unknown) =>
      setError((e as Error)?.message ?? 'Lost connection to the room.')

    const unsubRoom = onSnapshot(
      doc(db, 'rooms', roomCode),
      (snap) => {
        const data = snap.data()
        setRoom({
          name: String(data?.name ?? 'Live draw'),
          chatEnabled: data?.chatEnabled !== false,
          closed: data?.closed === true,
          startsAt: typeof data?.startsAt === 'number' ? data.startsAt : null,
          timeZone: typeof data?.timeZone === 'string' ? data.timeZone : null,
          lastWinner: data?.lastWinner
            ? { name: String(data.lastWinner.name), at: Number(data.lastWinner.at) || 0 }
            : null,
        })
      },
      onError,
    )

    const unsubPeople = onSnapshot(
      collection(db, 'rooms', roomCode, 'participants'),
      (snap) => {
        setParticipants(
          snap.docs.map((d) => ({
            id: d.id,
            name: String(d.data().name ?? 'Guest'),
            muted: d.data().muted === true,
            blocked: d.data().blocked === true,
          })),
        )
      },
      onError,
    )

    const unsubChat = onSnapshot(
      query(
        collection(db, 'rooms', roomCode, 'messages'),
        orderBy('createdAt', 'desc'),
        limit(MESSAGE_LIMIT),
      ),
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data()
          return {
            id: d.id,
            uid: String(data.uid ?? ''),
            name: String(data.name ?? 'Guest'),
            text: String(data.text ?? ''),
            createdAt: data.createdAt?.toMillis?.() ?? null,
          } satisfies ChatMessage
        })
        rows.sort((a, b) => (a.createdAt ?? Infinity) - (b.createdAt ?? Infinity))
        setMessages(rows)
      },
      onError,
    )

    const unsubWinners = onSnapshot(
      query(
        collection(db, 'rooms', roomCode, 'winners'),
        orderBy('createdAt', 'desc'),
        limit(200),
      ),
      (snap) => {
        setWinners(
          snap.docs.map((d) => ({
            id: d.id,
            name: String(d.data().name ?? ''),
            at: d.data().createdAt?.toMillis?.() ?? null,
          })),
        )
      },
      onError,
    )

    return () => {
      unsubRoom()
      unsubPeople()
      unsubChat()
      unsubWinners()
    }
  }, [roomCode])

  const me = useMemo(
    () => participants.find((p) => p.id === session?.uid) ?? null,
    [participants, session?.uid],
  )

  const sendMessage = useCallback(
    async (text: string) => {
      const body = text.trim().slice(0, 500)
      if (!roomCode || !session || !body) return
      await addDoc(collection(db, 'rooms', roomCode, 'messages'), {
        uid: session.uid,
        name: session.role === 'admin' ? 'Host' : session.name,
        host: session.role === 'admin',
        text: body,
        createdAt: serverTimestamp(),
      })
    },
    [roomCode, session],
  )

  const deleteMessage = useCallback(
    async (id: string) => {
      if (!roomCode || !isHost) return
      await deleteDoc(doc(db, 'rooms', roomCode, 'messages', id))
    },
    [isHost, roomCode],
  )

  const wipeChat = useCallback(async () => {
    if (!roomCode || !isHost) return
    const snap = await getDocs(collection(db, 'rooms', roomCode, 'messages'))
    const chunks: (typeof snap.docs)[] = []
    for (let i = 0; i < snap.docs.length; i += 400) {
      chunks.push(snap.docs.slice(i, i + 400))
    }
    for (const chunk of chunks) {
      const batch = writeBatch(db)
      chunk.forEach((d) => batch.delete(d.ref))
      await batch.commit()
    }
  }, [isHost, roomCode])

  /** Wipes the published winners so every participant screen resets too. */
  const clearWinners = useCallback(async () => {
    if (!roomCode || !isHost) return
    await setDoc(doc(db, 'rooms', roomCode), { lastWinner: null }, { merge: true })
    const snap = await getDocs(collection(db, 'rooms', roomCode, 'winners'))
    for (let i = 0; i < snap.docs.length; i += 400) {
      const batch = writeBatch(db)
      snap.docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref))
      await batch.commit()
    }
  }, [isHost, roomCode])

  const setChatEnabled = useCallback(
    async (enabled: boolean) => {
      if (!roomCode || !isHost) return
      await setDoc(doc(db, 'rooms', roomCode), { chatEnabled: enabled }, { merge: true })
    },
    [isHost, roomCode],
  )

  const setSchedule = useCallback(
    async (startsAt: number | null, timeZone: string) => {
      if (!roomCode || !isHost) return
      await setDoc(doc(db, 'rooms', roomCode), { startsAt, timeZone }, { merge: true })
    },
    [isHost, roomCode],
  )

  const setClosed = useCallback(
    async (closed: boolean) => {
      if (!roomCode || !isHost) return
      await setDoc(doc(db, 'rooms', roomCode), { closed }, { merge: true })
    },
    [isHost, roomCode],
  )

  const renameEvent = useCallback(
    async (name: string) => {
      if (!roomCode || !isHost) return
      await setDoc(doc(db, 'rooms', roomCode), { name: name.slice(0, 60) }, { merge: true })
    },
    [isHost, roomCode],
  )

  const setMuted = useCallback(
    async (uid: string, muted: boolean) => {
      if (!roomCode || !isHost) return
      await updateDoc(doc(db, 'rooms', roomCode, 'participants', uid), { muted })
    },
    [isHost, roomCode],
  )

  const setBlocked = useCallback(
    async (uid: string, blocked: boolean) => {
      if (!roomCode || !isHost) return
      await updateDoc(doc(db, 'rooms', roomCode, 'participants', uid), { blocked })
    },
    [isHost, roomCode],
  )

  const publishWinner = useCallback(
    async (name: string) => {
      if (!roomCode || !isHost) return
      const at = Date.now()
      await setDoc(doc(db, 'rooms', roomCode), { lastWinner: { name, at } }, { merge: true })
      await addDoc(collection(db, 'rooms', roomCode, 'winners'), {
        name,
        createdAt: serverTimestamp(),
      })
    },
    [isHost, roomCode],
  )

  return {
    room,
    participants,
    messages,
    winners,
    me,
    error,
    sendMessage,
    deleteMessage,
    wipeChat,
    clearWinners,
    setChatEnabled,
    setClosed,
    setSchedule,
    renameEvent,
    setMuted,
    setBlocked,
    publishWinner,
  }
}
